import type { PartnerResponse } from '@palaccount/contracts';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import type { TypeaheadOption } from '@/components/ui/typeahead';
import { useDebounced, usePickerState, type PickerState } from '@/lib/typeahead';

import { usePartnerSearch, usePartnersHeld, type PartnerListFilters } from './api';

/**
 * THE PARTNER PICKER (GEN-50) — the customer/supplier field of every document
 * editor, and PAGINATION.md §5a's first entry.
 *
 * `heldId` is the partner the form is holding (documents name exactly one),
 * and it is fetched separately from the search for the reason the component
 * spells out: a posted invoice must still show its customer when the reader
 * is searching for somebody else, or after that customer was switched off.
 *
 * INV-F10 — `filters.type` is the kind the FIELD expects, and it no longer
 * narrows the search: "the customer field must find suppliers too". A
 * supplier who buys once is a customer from that day (the server flips the
 * flag at posting), and a picker that hid them forced the user to duplicate
 * the record. So every active partner is offered, and a row that is not of
 * the expected kind says so beside its code — "(supplier)" on a customer
 * field — so the pick is informed rather than silent.
 */
export function usePartnerPicker(
  heldId: string | null | undefined,
  filters: PartnerListFilters = {},
  /**
   * FIN-03 §5 and FIN-04 §5 ask for a third thing on the row — what the
   * partner still owes, or is owed — so the row builder is overridable. The
   * SEARCH is unaffected: it matches name and code either way.
   */
  toOption: (partner: PartnerResponse) => TypeaheadOption = partnerOption,
): PickerState<PartnerResponse> {
  const { t } = useTranslation();
  const [query, setQuery] = React.useState('');
  const search = useDebounced(query);
  const ids = React.useMemo(() => (heldId ? [heldId] : []), [heldId]);

  const { type: expected, ...searchFilters } = filters;
  const matches = usePartnerSearch(search, searchFilters);
  const held = usePartnersHeld(ids);

  const toMarkedOption = React.useCallback(
    (partner: PartnerResponse): TypeaheadOption => {
      const option = toOption(partner);
      const marker = otherKindMarker(partner, expected);
      if (marker === null) return option;
      const word = t(marker === 'customer' ? 'partners.typeCustomer' : 'partners.typeSupplier');
      return { ...option, hint: [option.hint, `(${word})`].filter(Boolean).join(' · ') };
    },
    [toOption, expected, t],
  );

  return usePickerState<PartnerResponse>({
    query,
    setQuery,
    search,
    matches: matches.data,
    held: held.data,
    toOption: toMarkedOption,
    loading: matches.isFetching,
  });
}

/**
 * The kind to print beside a row that is NOT what the field asked for: a
 * supplier-only record on a customer field reads "(supplier)", and vice versa.
 * A partner who is already both, or a field with no expectation, gets nothing.
 */
export function otherKindMarker(
  partner: Pick<PartnerResponse, 'isCustomer' | 'isSupplier'>,
  expected: 'customer' | 'supplier' | undefined,
): 'customer' | 'supplier' | null {
  if (expected === 'customer' && !partner.isCustomer && partner.isSupplier) return 'supplier';
  if (expected === 'supplier' && !partner.isSupplier && partner.isCustomer) return 'customer';
  return null;
}

/**
 * MD-01 §6's own two columns: the name, and the code beside it. §24.4's
 * partner combobox describes exactly this shape — "the name, the code and
 * type beneath" — and the search box behind it matches both.
 */
export function partnerOption(partner: PartnerResponse): TypeaheadOption {
  return {
    id: partner.id,
    label: partner.name,
    hint: partner.code,
    inactive: !partner.isActive,
  };
}
